/**
 * AI Clone / Digital Twin - Rebrand of Chatbot Builder
 * Train AI on YOUR content to create your digital twin
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Upload, Bot, FileText, Loader2, CheckCircle, 
  AlertCircle, Sparkles, Code, Link, ArrowRight,
  User, Brain, Zap, Copy
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AIClone() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (selectedFile) => {
    const validExtensions = ['.pdf', '.txt'];
    const hasValidExtension = validExtensions.some(ext => 
      selectedFile.name.toLowerCase().endsWith(ext)
    );
    
    if (!hasValidExtension) {
      setError('Please upload a PDF or TXT file');
      return;
    }
    
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File too large. Max 10MB');
      return;
    }
    
    setFile(selectedFile);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/auth?redirect=/ai-clone');
      return;
    }
    
    if (!name.trim()) {
      setError('Please name your AI clone');
      return;
    }
    
    if (!file) {
      setError('Please upload your content');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('name', name + "'s AI Clone");
      formData.append('description', description || `AI trained on ${name}'s content and style`);
      formData.append('file', file);
      
      const response = await axios.post(`${API}/chatbots/create`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create AI clone');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900/50 border border-purple-500/30 rounded-2xl p-8 text-center"
          >
            <div className="w-20 h-20 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Brain className="w-10 h-10 text-purple-400" />
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2">
              Your AI Clone is Alive!
            </h1>
            <p className="text-zinc-400 mb-8">
              {success.name} has absorbed {success.chunks_created} pieces of your knowledge.
            </p>
            
            <div className="space-y-4 text-left">
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-purple-400 mb-2">
                  <Link className="w-4 h-4" />
                  <span className="font-medium">Share Your Clone</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={`${window.location.origin}/chat/${success.public_id}`}
                    className="bg-zinc-900 border-zinc-700 text-white font-mono text-sm"
                  />
                  <Button
                    onClick={() => navigator.clipboard.writeText(`${window.location.origin}/chat/${success.public_id}`)}
                    className="bg-purple-500 hover:bg-purple-600"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-pink-400 mb-2">
                  <Code className="w-4 h-4" />
                  <span className="font-medium">Embed on Your Website</span>
                </div>
                <div className="bg-zinc-900 rounded-lg p-3 font-mono text-xs text-zinc-300 overflow-x-auto">
                  {`<iframe src="${window.location.origin}/api/chatbots/embed/${success.public_id}" width="400" height="600"></iframe>`}
                </div>
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <Button
                onClick={() => navigate(`/chat/${success.public_id}`)}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400"
              >
                Talk to Your Clone
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => {
                  setSuccess(null);
                  setName('');
                  setFile(null);
                }}
                variant="outline"
                className="border-zinc-700 text-zinc-300"
              >
                Create Another
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-2xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Brain className="w-10 h-10 text-purple-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Create Your AI Clone
          </h1>
          <p className="text-zinc-400 text-lg">
            Upload your content. Get an AI that thinks, writes, and responds
            <span className="text-purple-400 font-semibold"> exactly like you.</span>
          </p>
        </motion.div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-4 mb-8"
        >
          {[
            { icon: Upload, text: "Upload your content" },
            { icon: Brain, text: "AI learns your style" },
            { icon: Zap, text: "Clone responds like you" }
          ].map((step, i) => (
            <div key={i} className="text-center p-4 bg-zinc-900/30 rounded-xl border border-zinc-800">
              <step.icon className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">{step.text}</p>
            </div>
          ))}
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8"
        >
          <div className="mb-6">
            <label className="block text-white font-medium mb-2">
              Your Name *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., John, Sarah, Dr. Smith"
              className="bg-zinc-800 border-zinc-700 text-white h-12"
            />
            <p className="text-zinc-500 text-sm mt-1">This will be "[Name]'s AI Clone"</p>
          </div>

          <div className="mb-6">
            <label className="block text-white font-medium mb-2">
              What should your clone know? <span className="text-zinc-500">(optional)</span>
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Answer questions about my business, respond to customer inquiries in my voice"
              className="bg-zinc-800 border-zinc-700 text-white min-h-[80px]"
            />
          </div>

          <div className="mb-6">
            <label className="block text-white font-medium mb-2">
              Upload Your Content *
            </label>
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                dragActive 
                  ? 'border-purple-500 bg-purple-500/10' 
                  : file 
                    ? 'border-green-500/50 bg-green-500/10' 
                    : 'border-zinc-700 hover:border-zinc-600'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-green-400" />
                  <div className="text-left">
                    <p className="text-white font-medium">{file.name}</p>
                    <p className="text-zinc-500 text-sm">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <Button type="button" variant="ghost" onClick={() => setFile(null)} className="text-zinc-400">
                    Change
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-zinc-500 mx-auto mb-4" />
                  <p className="text-zinc-300 mb-2">
                    Drop your content here
                  </p>
                  <label className="cursor-pointer">
                    <span className="text-purple-400 hover:text-purple-300">browse to upload</span>
                    <input
                      type="file"
                      accept=".pdf,.txt"
                      onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}
                      className="hidden"
                    />
                  </label>
                  <p className="text-zinc-500 text-sm mt-4">
                    Upload blog posts, emails, documents, or any text that represents your voice
                  </p>
                </>
              )}
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-400 mb-6 bg-red-500/10 p-3 rounded-lg">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading || !name || !file}
            className="w-full h-12 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white font-semibold rounded-xl"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Creating your clone...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Create My AI Clone
              </>
            )}
          </Button>

          {!user?.is_subscriber && (
            <p className="text-center text-zinc-500 text-sm mt-4">
              Free users can create 1 AI clone.{' '}
              <button type="button" onClick={() => navigate('/subscription')} className="text-purple-400 hover:text-purple-300">
                Upgrade for unlimited
              </button>
            </p>
          )}
        </motion.form>
      </div>
    </div>
  );
}
